/**
 * Reading back the reference anchors a prompt stores (see `chipsToAnchors` in ./utils.js):
 * `<a href data-type data-id>Title</a>` → the `{ type, id, details }` a chip renders from, so a sent
 * message and an edited one show chips again.
 *
 * `details` comes from the docs Redux holds, else from the anchor: its text is the title and its
 * link names the board and account. A board the user can open is named from
 * `user-accessible-boards` even when it was never loaded.
 */
import * as firestoreRedux from '@dreamworld/firestore-redux';
import { parseReferenceLink, CHIP_TAG_BY_TYPE, referenceTypeOf } from './utils.js';
import { getUserName } from '../../components/utils.js';

/** The AI Helper's scope chip: no reference, so not in `CHIP_TAG_BY_TYPE`. */
const IMPLICIT_CONTEXT_CHIP_TAG = 'implicit-context-chip-item';

/** The `user-accessible-boards` entry of `boardId`: names a board the user can open even when it was never loaded. */
export const accessibleBoard = (state, boardId) =>
  Object.values(firestoreRedux.selectors.collection(state, 'user-accessible-boards') || {}).find((b) => b.boardId === boardId);

/**
 * A reference anchor's `{ type, id, details }` from its parts: its `data-type`, `data-id`, `href` and text, and a
 * file's `data-board-id` / `data-card-id`.
 * @returns {Object|null} `{ type, id, details }`, or null for an unknown type
 */
export const referenceOf = ({ dataType, id, href, title: text, boardId: fileBoardId, cardId: fileCardId }, state) => {
  const type = referenceTypeOf(dataType);
  const title = (text || '').trim();
  if (type === 'SCOPE' && id) {
    return { type: 'IMPLICIT_CONTEXT', id, details: { title } };
  }
  if (!CHIP_TAG_BY_TYPE[type] || !id) {
    return null;
  }

  const link = parseReferenceLink(href || '');
  const doc = (collection, docId) => (docId ? firestoreRedux.selectors.doc(state, collection, docId) : undefined);
  const boardRef = (boardId) => {
    if (!boardId) return undefined;
    const board = doc('boards', boardId);
    return {
      id: boardId,
      name: board?.name || accessibleBoard(state, boardId)?.boardName,
      accountId: board?.accountId || accessibleBoard(state, boardId)?.accountId || link?.accountId,
    };
  };
  const columnRef = (card) => {
    const column = doc('columns', card?.columnId);
    return column ? { id: card.columnId, name: column.name } : undefined;
  };
  // A sub-task's or file's link is its card's page, so the card id is the link's own id.
  const linkedCardId = link?.type === 'CARD' ? link.id : undefined;

  switch (type) {
    case 'CARD': {
      const card = doc('cards', id);
      return { type, id, details: { title: card?.title || title, board: boardRef(card?.boardId || link?.boardId), column: columnRef(card) } };
    }
    case 'BOARD': {
      const board = boardRef(id);
      return { type, id, details: { name: board.name || title, accountId: board.accountId } };
    }
    case 'SUBTASK': {
      const task = doc('tasks', id);
      const cardId = task?.cardId || linkedCardId;
      const card = doc('cards', cardId);
      return {
        type,
        id,
        details: {
          title: task?.title || title,
          board: boardRef(card?.boardId || link?.boardId),
          column: columnRef(card),
          card: cardId ? { id: cardId, title: card?.title } : undefined,
        },
      };
    }
    case 'FILE': {
      if (id.startsWith('aca_')) {
        // An AI Helper upload: the chip reads its own status and file from Firestore.
        return { type, id, details: { title, ...(doc('ai-chat-attachments', id) || {}) } };
      }
      const file = doc('card-attachments', id) || doc('board-attachments', id);
      const cardId = file?.cardId || fileCardId || linkedCardId;
      return {
        type,
        id,
        details: {
          title: file?.title || title,
          type: file?.type,
          subType: file?.subType,
          url: file?.url,
          board: boardRef(file?.boardId || fileBoardId || link?.boardId),
          card: cardId ? { id: cardId, title: doc('cards', cardId)?.title } : undefined,
        },
      };
    }
    case 'TAG': {
      const tag = doc('board-tags', id);
      return { type, id, details: { name: tag?.name || title, color: tag?.color, board: boardRef(tag?.boardId || link?.boardId) } };
    }
    case 'AICHAT': {
      const chat = doc('ai-chats', id);
      const cardId = chat?.context?.cardId || link?.cardId;
      return {
        type,
        id,
        details: {
          title: chat?.title || title,
          board: boardRef(chat?.context?.boardId || link?.boardId),
          card: cardId ? { id: cardId, title: doc('cards', cardId)?.title } : undefined,
        },
      };
    }
    case 'USER': {
      const user = doc('users', id);
      const [firstName, ...rest] = title.split(/\s+/);
      return {
        type,
        id,
        details: {
          firstName: user?.firstName ?? firstName,
          lastName: user?.lastName ?? rest.join(' '),
          email: user?.email || '',
          profileImage: user?.profileImage || '',
        },
      };
    }
    default:
      return null;
  }
};

/**
 * The chip element (in `doc`) for a reference anchor, or null: `item-id` and `details` set as
 * attributes, the anchor's text as its own text — never parsed as HTML.
 *
 * @param {Element} anchor - A reference anchor
 * @param {Document} doc - Document to create the chip in
 * @param {Object} state - Redux state
 */
export const chipFromAnchor = (anchor, doc, state) => {
  const reference = referenceOf(
    {
      dataType: anchor.getAttribute('data-type'),
      id: anchor.getAttribute('data-id'),
      href: anchor.getAttribute('href'),
      title: anchor.textContent,
      boardId: anchor.getAttribute('data-board-id'),
      cardId: anchor.getAttribute('data-card-id'),
    },
    state
  );
  if (!reference) {
    return null;
  }
  const chip = doc.createElement(reference.type === 'IMPLICIT_CONTEXT' ? IMPLICIT_CONTEXT_CHIP_TAG : CHIP_TAG_BY_TYPE[reference.type]);
  chip.setAttribute('item-id', reference.id);
  chip.setAttribute('details', JSON.stringify(reference.details || {}));
  chip.textContent = anchor.textContent;
  return chip;
};

/**
 * Replaces each reference anchor in `root` that `chipOf` gives a chip for with that chip.
 *
 * @param {Element} root - Element whose anchors are replaced
 * @param {Function} chipOf - `anchor` → its chip, or null to keep the anchor
 * @param {string} [selector] - The anchors to try
 * @returns {Object[]} `resolveReferences`' `{ type, id, url, boardId, cardId }` of each reference chip drawn
 */
export const replaceAnchorsWithChips = (root, chipOf, selector = 'a[data-type][data-id]') => {
  const chipReferences = [];
  root.querySelectorAll(selector).forEach((anchor) => {
    const chip = chipOf(anchor);
    if (!chip) {
      return;
    }
    const type = referenceTypeOf(anchor.getAttribute('data-type'));
    CHIP_TAG_BY_TYPE[type] &&
      chipReferences.push({
        type,
        id: anchor.getAttribute('data-id'),
        url: anchor.getAttribute('href'),
        boardId: anchor.getAttribute('data-board-id'),
        cardId: anchor.getAttribute('data-card-id'),
      });
    anchor.replaceWith(chip);
  });
  return chipReferences;
};

/**
 * `toEditorValue`'s `titleOf` from the docs Redux holds: a card's, board's or AI chat's title, a user's name.
 * @param {Object} state - Redux state
 * @returns {Function} `({ type, id })` → the title, or undefined when the doc is not loaded
 */
export const titleOf = (state) => ({ type, id }) => {
  const doc = (collection) => firestoreRedux.selectors.doc(state, collection, id);
  switch (type) {
    case 'CARD':
      return doc('cards')?.title;
    case 'BOARD':
      return doc('boards')?.name || accessibleBoard(state, id)?.boardName;
    case 'AICHAT':
      return doc('ai-chats')?.title;
    case 'USER':
      return getUserName(doc('users')) || undefined;
    default:
      return undefined;
  }
};
